<script lang="ts" setup>
import { computed, provide, onMounted, ref, watch } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import marketTypeTradingEligibilityService from '@exchange/libs/account/service/src/lib/market-type-trading-eligibility.service';
import { authService } from '@exchange/libs/auth/service/src';
import { balanceService } from '@exchange/libs/balances/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { featureRestrictionsService } from '@exchange/libs/feature-restrictions/service/src';
import futuresFeatureFlagService from '@exchange/libs/futures/service/src/lib/futures-feature-flag.service';
import { marketService, MarketState } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import BlockedOverlay from '@exchange/libs/overlays/BlockedOverlay.vue';
import FuturesPublicTradingDisabledOverlay from '@exchange/libs/overlays/FuturesPublicTradingDisabledOverlay.vue';
import IneligibleToTradeMarketOverlay from '@exchange/libs/overlays/IneligibleToTradeMarketOverlay.vue';
import LoggedOutOverlay from '@exchange/libs/overlays/LoggedOutOverlay.vue';
import MarketMismatchOverlay from '@exchange/libs/overlays/MarketMismatchOverlay.vue';
import MarketOverlay from '@exchange/libs/overlays/MarketOverlay.vue';
import { subaccountsService, SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';

import orderFormEmitter from './order-form-event-bus';
import OrderForm from './OrderForm.vue';
import OrderFormSideSwitcher from './OrderFormSideSwitcher.vue';
import OrderTypeChooser from './OrderTypeChooser.vue';
import useOrderTypes from './OrderTypes';

const props = defineProps({
  marketId: { type: String, required: true },
  transparentBackground: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'reset-form-softly'): void;
  (e: 'order-form:scroll-content', v: number): void;
}>();

provide('orderFormEmitter', orderFormEmitter);

const { isXS } = useMQBreakpoints();

const { storedType, market: marketType, limit: limitType } = useOrderTypes();

const supportMarketOrders = launchdarkly.flags['market-orders'];
const { balanceLoadingUntil } = balanceService;
const { isEnabledWithPublicTradingEnabled: isFuturesEnabledWithPublicTradingEnabled, isReady: isFuturesFeatureFlagReady } = futuresFeatureFlagService;
const { getMarket, getMarketName, getMarketState } = marketService;

const orderContainerEl = ref<undefined | { $el: HTMLElement }>();
const orderContainer = ref<{
  topPosition: number;
}>({
  topPosition: 0,
});

let psScrollTriggered = 0;
const typeOptions = computed(() => (supportMarketOrders.value ? [marketType, limitType] : [limitType]));

const market = computed(() => getMarket(props.marketId));
const marketName = computed(() => getMarketName(market.value));
const marketState = computed(() => getMarketState(market.value));

const showMarketOverlayOnForm = computed(() => marketState.value && [MarketState.SUSPENDED, MarketState.IN_MAINTENANCE, MarketState.NEW].includes(marketState.value));
const { isSettingAccount, subaccountType, userIsBlocked: isBlocked } = accountService;
const { isSwitchingSubaccount } = subaccountsService;
const isLoading = computed(() => !authService.initialLoadAuthIsChecked || !market.value || isSwitchingSubaccount.value);
const userHasBeenAuthenticated = computed(() => authService.hasBeenAuthenticated);

const isFuturesMarket = computed(() => market.value?.type === MarketType.PERP);
const isSpotMarket = computed(() => market.value?.type === MarketType.SPOT);

const canShowAccountOverlay = computed(() => !isLoading.value && !isSettingAccount.value);
const canShowFuturesPublicTradingDisabledOverlay = computed(() => isFuturesFeatureFlagReady.value);

const { isLoadingMarketTypeTradingEligibility, marketTypeTradingEligibility, canTradeSpot, canTradeFutures } = marketTypeTradingEligibilityService;

const showFuturesPublicTradingDisabledOverlay = computed(() => {
  if (isSpotMarket.value || !canShowFuturesPublicTradingDisabledOverlay.value) {
    return false;
  }

  return !isFuturesEnabledWithPublicTradingEnabled.value;
});

const showIneligibleToTradeMarketOverlay = computed(() => {
  if (!canShowAccountOverlay.value || isLoadingMarketTypeTradingEligibility.value || !marketTypeTradingEligibility.value) return false;

  return isSpotMarket.value ? !canTradeSpot.value : !canTradeFutures.value;
});

const showAccountTypeMismatchOverlay = computed(() => {
  if (!canShowAccountOverlay.value || !subaccountType.value) return false;

  return (isFuturesMarket.value && subaccountType.value !== SubaccountType.FUTURES) || (isSpotMarket.value && subaccountType.value !== SubaccountType.SPOT);
});

const activeType = computed(() => storedType.value || limitType.type);

const scrollCallbacks = {
  onScroll: () => {
    psScrollTriggered += 1;
  },
};

const onActiveTypeChange = (type) => {
  if (storedType.value !== type) {
    orderformService.setType(type);
    orderFormEmitter.emit('reset-form-softly');
  }
};

const onActiveSideChange = () => {
  emit('order-form:scroll-content', orderContainer.value?.topPosition);
};

const setContainerTopPosition = () => {
  const elem = orderContainerEl.value;

  if (!elem) {
    return;
  }

  const { top } = elem.$el.getBoundingClientRect();

  orderContainer.value.topPosition = top;
};

watch(
  userHasBeenAuthenticated,
  (newUserHasBeenAuthenticated) => {
    if (newUserHasBeenAuthenticated) {
      featureRestrictionsService.get();
    }
  },
  {
    immediate: true,
  },
);

onMounted(setContainerTopPosition);
</script>

<template>
  <ot-module-container
    ref="orderContainerEl"
    class="order-container"
    testid="order-container"
    log-name="order-form"
    :scroll-callbacks="scrollCallbacks"
    :variant="transparentBackground ? 'transparent' : 'default'"
    :loading="isLoading"
  >
    <template #header-left-actions>
      <order-type-chooser
        class="order-container__type-chooser"
        :available-types="typeOptions"
        :selected-type="activeType"
        :class="{ 'order-container__type-chooser--mobile': isXS }"
        :is-futures="isFuturesMarket"
        @type-selected="onActiveTypeChange"
      />
    </template>
    <template #main-content>
      <div class="order-container__content">
        <logged-out-overlay v-if="!userHasBeenAuthenticated" />
        <blocked-overlay v-else-if="isBlocked" />
        <market-overlay
          v-else-if="showMarketOverlayOnForm"
          :market-name="marketName"
          :market-state="marketState"
          :has-been-authenticated="userHasBeenAuthenticated"
        />
        <futures-public-trading-disabled-overlay v-else-if="showFuturesPublicTradingDisabledOverlay" />
        <ineligible-to-trade-market-overlay v-else-if="showIneligibleToTradeMarketOverlay" />
        <market-mismatch-overlay
          v-else-if="showAccountTypeMismatchOverlay"
          :is-futures-market="isFuturesMarket"
        />
        <div class="order-container__order-form">
          <order-form-side-switcher
            class="order-container__side-switcher"
            :class="{ 'order-container__side-switcher--mobile': isXS }"
            :on-active-side-change="onActiveSideChange"
            :is-futures="isFuturesMarket"
          />
          <order-form
            v-if="market"
            class="order-container__form"
            :transparent="transparentBackground"
            :fit-slider-tooltip-max-value="!isXS"
            :balance-loading-until="balanceLoadingUntil"
            :spot-market="market"
            :ps-scroll-triggered="psScrollTriggered"
            :is-futures="isFuturesMarket"
          />
        </div>
      </div>
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.order-container {
  position: relative;
  display: flex;
  height: 100%;
  flex-direction: column;
  padding-right: 0;
  padding-left: 0;
  overflow-y: auto;
  user-select: text;

  &__content {
    display: flex;
    flex-direction: column;

    .xs & {
      padding: 0;
    }
  }

  &__account-switch {
    margin-bottom: 8px;
  }

  &__funding-actions {
    padding-bottom: 8px;
    border-bottom: 2px solid rgb(var(--v-theme-elevation-1));
  }

  &__side-switcher {
    flex: 0 0 auto;
    margin: 8px 0 20px;

    &--mobile {
      margin-top: 0;
    }
  }

  &__form {
    flex: 1 0 auto;
  }
}
</style>
